import { FC } from 'react';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import { config, animated, Spring } from 'react-spring';
import styled from 'styled-components';
import { space } from 'styled-system';
import { Flex, PoweredByPromo } from '../../../components/shared';
import { PageWrap } from '../../../layouts';
import { StyledSystemProps } from '../../../theme/StyledSystemProps';
import { H3 } from '../../../typography';
import {
  isDSDRoute,
  isCapsiRoute,
  getCustomBackgroundImage,
  getCustomLogoImage,
  isMobile,
  isTablet,
} from '../../../utils';
import LoginForm from './Login/LoginForm';
import { SCREEN } from '../../../theme/breakpoints';

const isWeb = SCREEN.isWeb;

type LandingPageProps = RouteComponentProps & {};

type PanelWrapperProps = StyledSystemProps & {};

type LogoProps = {
  isTablet: boolean;
  isWeb: boolean;
};

const PanelWrapper = styled(animated.div)<PanelWrapperProps>`
  ${space};
  top: 0;
  left: 0;
  bottom: 0;
  width: ${isWeb ? '350px' : 'calc(100vw / var(--ib-ui-scale, 1))'};
  display: flex;
  z-index: 9995;
  max-width: 100%;
  position: fixed;
  overflow-y: ${isWeb ? 'auto' : 'hidden'};
  background: white;
  align-items: center;
  box-sizing: border-box;
  flex-direction: column;
  height: ${isWeb ? '100%' : 'calc(100vh / var(--ib-ui-scale, 1))'};
  justify-content: ${isWeb ? 'center' : 'flex-start'};
  box-shadow: 5px 0px 10px -8px rgba(0, 0, 0, 0.4), -12px 0 8px -4px rgba(0, 0, 0, 0.2);
  & > * {
    max-width: 520px;
  }
`;

const Logo = styled.img<LogoProps>`
  display: inline-block;
  max-width: 100%;
  height: auto;
  width: ${(props: any) =>
    props.isWeb ? '60%' : props.isTablet ? 'calc(35vw / var(--ib-ui-scale, 1))' : 'calc(45vw / var(--ib-ui-scale, 1))'};
  /* The stacked (tablet/mobile) layout sizes the logo off the window width but holds it in a
     130px box, so widening the window after load let it grow over the heading (IB-1885). */
  max-height: ${(props: any) => (props.isWeb ? 'none' : '100%')};
  object-fit: contain;
`;

const Login: FC<LandingPageProps> = () => {
  const customBackgroundImage = getCustomBackgroundImage();
  const customLogoImage: any = getCustomLogoImage();

  return (
    <PageWrap
      height="calc(100vh / var(--ib-ui-scale, 1))"
      backgroundImage={isWeb ? customBackgroundImage : null}
      width="calc(100vw / var(--ib-ui-scale, 1))"
    >
      {isWeb && (
        <Spring
          native={true}
          delay={500}
          config={config.default}
          from={{ opacity: 0, transform: 'translateX(-300px)' }}
          to={{ opacity: 1, transform: 'translateX(0)' }}
        >
          {springProps => (
            <PanelWrapper p={5} style={springProps}>
              <Logo src={customLogoImage} isWeb={isWeb} isTablet={isTablet} />
              <H3>Welcome back! Login now.</H3>

              <LoginForm />
              <Flex direction="row" justify="center" size="0.25" alignItems="center">
                <p style={{ ...(isMobile && { fontSize: '0.8em' }), textAlign: 'center' }}>
                  Don't have an account? Contact your administrator or <Link to="/signup">Sign Up</Link>
                </p>
              </Flex>
              {(isDSDRoute || isCapsiRoute) && <PoweredByPromo />}
            </PanelWrapper>
          )}
        </Spring>
      )}

      {!isWeb && (
        <PanelWrapper p={isMobile ? 4 : 130}>
          <div
            style={{
              alignItems: 'center',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: '130px',
              padding: '20px 0',
            }}
          >
            <Logo src={customLogoImage} isWeb={isWeb} isTablet={isTablet} />
          </div>

          <p style={{ fontWeight: 600, textAlign: 'center', ...(isTablet && { fontSize: '1.5em' }) }}>
            Welcome back! Login now.
          </p>
          <LoginForm />
          <Flex direction="row" justify="center" size="0.25" alignItems="center">
            <p style={{ ...(isMobile && { fontSize: '0.8em' }), textAlign: 'center' }}>
              Don't have an account? Contact your administrator or <Link to="/signup">Sign Up</Link>
            </p>
          </Flex>
          {(isDSDRoute || isCapsiRoute) && <PoweredByPromo />}
        </PanelWrapper>
      )}
    </PageWrap>
  );
};

export default Login;
